<script lang="ts" setup>
  import Button from '@leaflink/stash/Button.vue';
  import Icon from '@leaflink/stash/Icon.vue';
  import Modal from '@leaflink/stash/Modal.vue';
  import { storeToRefs } from 'pinia';
  import { computed, ref } from 'vue';

  import { useCompanyStore } from '@/piniaStores/company/company.store';
  import { useUserStore } from '@/piniaStores/user/user.store';
  import i18n from '@/plugins/i18n';
  import { trackAcceptTermsAndConditions } from '@/segment';

  import BuyerDirectPayTerms from './BuyerDirectPayTerms.vue';
  import SellerDirectPayTerms from './SellerDirectPayTerms.vue';

  interface TermsAndConditionsConfirmationProps {
    isOpen: boolean;
  }

  const companyStore = useCompanyStore();
  const userStore = useUserStore();

  const emit = defineEmits<{
    (e: 'accepted'): void;
    (e: 'dismiss'): void;
  }>();

  const props = defineProps<TermsAndConditionsConfirmationProps>();

  const isAccepting = ref(false);

  const { user } = storeToRefs(userStore);
  const { activeCompany } = storeToRefs(companyStore);

  const isBuyer = computed(() => activeCompany.value?.buyer && !activeCompany.value?.seller);
  const isSeller = computed(() => activeCompany.value?.seller && !activeCompany.value?.buyer);

  const modalTitle = computed(() => {
    if (isBuyer.value) {
      return i18n.t('termsAndConditions.directPayTermsConfirmation.buyer.modalHeader');
    }

    if (isSeller.value) {
      return i18n.t('termsAndConditions.directPayTermsConfirmation.seller.modalHeader');
    }

    return i18n.t('termsAndConditions.self');
  });

  const updateUserSignedTcDate = async (dateSignedTc: string) => {
    await userStore.updateUser({
      data: {
        dateSignedTc,
      },
    });
  };

  const onAccept = async () => {
    try {
      isAccepting.value = true;

      await updateUserSignedTcDate(new Date().toISOString().split('T')[0]);
    } finally {
      isAccepting.value = false;
    }

    trackAcceptTermsAndConditions({
      user_id: user.value.id,
      company_id: activeCompany.value?.id,
      group_id: activeCompany.value?.group?.id || null,
    });

    emit('accepted');
  };
</script>

<template>
  <Modal
    data-test="modal|direct-pay-terms"
    size="narrow"
    scrollable
    :open="props.isOpen"
    :title="modalTitle"
    @dismiss="emit('dismiss')"
  >
    <SellerDirectPayTerms v-if="isSeller" />
    <BuyerDirectPayTerms v-if="isBuyer" />

    <template #actions>
      <Button secondary :disabled="isAccepting" @click="emit('dismiss')"> {{ i18n.t('common.nevermind') }} </Button>
      <Button :disabled="isAccepting" @click="onAccept">
        <Icon v-if="isAccepting" name="working" class="fx-spin" />
        <span v-else>{{ i18n.t('common.accept') }}</span>
      </Button>
    </template>
  </Modal>
</template>
